import { Helmet } from '@dr.pogodin/react-helmet'
import { Box, Button, Container, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import gtm from '@/lib/gtm'

/**
 * Fallback shown when a route is gated behind an org feature flag that is not
 * enabled for the active organisation. The nav link is already hidden in this
 * case; this covers users who reach the URL directly. `featureName`, when
 * provided by the guard, names the specific feature so the message isn't
 * generic.
 */
const FeatureUnavailable = ({ featureName }) => {
    const theme = useTheme()
    const mobileDevice = useMediaQuery(theme.breakpoints.down('sm'))

    useEffect(() => {
        gtm.push({ event: 'page_view' })
    }, [])

    const subject = featureName ? `The ${featureName} feature` : 'This feature'

    return (
        <>
            <Helmet>
                <title>
                    {featureName
                        ? `${featureName} unavailable`
                        : 'Feature unavailable'}
                </title>
            </Helmet>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    minHeight: '100%',
                    px: 3,
                    py: '80px'
                }}
            >
                <Container maxWidth="lg">
                    <Typography
                        align="center"
                        color="textPrimary"
                        variant={mobileDevice ? 'h4' : 'h1'}
                    >
                        {featureName
                            ? `${featureName} isn't available`
                            : 'Feature unavailable'}
                    </Typography>
                    <Typography
                        align="center"
                        color="textSecondary"
                        sx={{ mt: 0.5 }}
                        variant="subtitle2"
                    >
                        {subject} isn&apos;t enabled for your organisation. Ask
                        your administrator to enable it, or contact support if
                        you think this is a mistake.
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mt: 6
                        }}
                    >
                        <Button
                            color="primary"
                            component={RouterLink}
                            to="/"
                            variant="outlined"
                        >
                            Back to Home
                        </Button>
                    </Box>
                </Container>
            </Box>
        </>
    )
}

FeatureUnavailable.propTypes = {
    featureName: PropTypes.string
}

export default FeatureUnavailable
